import { TreeViewBaseItem } from "@mui/x-tree-view";
import { TFunction } from "i18next";
import { match } from "ts-pattern";

import {
    InsightsAsset,
    InsightsPropertyTree,
    PropertyObjectType,
} from "graphql-types/graphql";
import { getPropertyObjectTypeTranslation } from "utils/translations";

export const DETAILS_COLUMNS = [
    { key: "name" as const },
    { key: "value" as const },
];

export const EPC_PROPOSAL_COLUMNS: {
    key:
        | "category"
        | "initiative"
        | "description"
        | "cost"
        | "impact"
        | "profitability";
    label: string;
}[] = [
    { key: "category", label: "category" },
    { key: "initiative", label: "initiative" },
    { key: "description", label: "description" },
    { key: "cost", label: "cost" },
    { key: "impact", label: "impact" },
    { key: "profitability", label: "profitability" },
];

export const formatPropertyTree = (
    properties: InsightsPropertyTree[],
    parentId?: string
): TreeViewBaseItem[] =>
    properties.map((property) => {
        const children = property.children
            ? formatPropertyTree(property.children, property.id)
            : undefined;

        const objectType = getPropertyObjectTypeTranslation(
            property.objectType
        );

        return {
            id: parentId ? `${parentId}-${property.id}` : property.id,
            label: `${property.fullAddress} (${objectType})`,
            children,
        };
    });

type keyofProperty = keyof InsightsPropertyTree;

const getDetailsRows = (
    t: TFunction
): (
    | {
          label: string;
          key: Exclude<keyofProperty, "children">;
      }
    | {
          label: string;
          key: "children";
          formatter: (value: InsightsPropertyTree[]) => TreeViewBaseItem[];
      }
)[] => [
    { label: t("legacyNext.address", "address"), key: "fullAddress" },
    { label: "BFE", key: "id" },
    {
        label: t("assetDetailsComponent.propertyList.title", "properties"),
        key: "children",
        formatter: formatPropertyTree,
    },
    {
        label: t("assetDetailsComponent.propertyType", "property type"),
        key: "propertyType",
    },
    {
        label: t("assetDetailsComponent.constructionYear", "construction year"),
        key: "constructionYear",
    },
    {
        label: t(
            "assetDetailsComponent.nationalPropertyType",
            "national property type"
        ),
        key: "nationalPropertyType",
    },
];

export const getDetailsRowValues = (asset: InsightsAsset, t: TFunction) => {
    const rows = getDetailsRows(t);

    const firstBuilding = getAssetBuildings(
        asset.properties as InsightsPropertyTree[]
    )[0];

    return rows.map((row) => {
        const value = match(row)
            .with({ key: "children" }, (row) => row.formatter(asset.properties))
            .with({ key: "id" }, () => asset.externalId)
            .otherwise((row) => firstBuilding[row.key]);

        return {
            name: row.label,
            value: value ?? "-",
        };
    });
};

export function getAssetBuildings(
    properties: InsightsPropertyTree[]
): InsightsPropertyTree[] {
    const buildings: InsightsPropertyTree[] = [];

    const traverse = (properties: InsightsPropertyTree[]) => {
        properties.forEach((property) => {
            if (property.objectType === PropertyObjectType.BUILDING)
                buildings.push(property);
            if (property.children) traverse(property.children);
        });
    };

    traverse(properties);
    return buildings;
}

export const flat = (arr: InsightsPropertyTree[]): InsightsPropertyTree[] =>
    arr.flatMap((item) => [
        item,
        ...(item.children ? flat(item.children) : []),
    ]);
